/**
 * AudioRecorder Component
 * Handles audio recording using browser's Web Audio API
 * Compatible with Node.js 16.20.2
 */

import React, { useState, useRef, useEffect } from 'react';
import { transcribeAudio } from '../services/sarvamApi';
import '../styles/AudioRecorder.css';

function AudioRecorder({ onTranscriptReceived, onError, onProcessingStart, isDisabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationIdRef = useRef(null);

  /**
   * Cleanup function for recording
   */
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  /**
   * Start recording audio from the user's microphone
   */
  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;
      chunksRef.current = [];

      // Set up audio analysis for visualization
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      // Create MediaRecorder
      // Use a MIME type that's supported by the browser
      let mimeType = 'audio/webm';
      const supportedTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/wav',
      ];

      mimeType = supportedTypes.find((type) =>
        MediaRecorder.isTypeSupported(type)
      );

      if (!mimeType) {
        console.warn(
          'No supported MIME type found. Using default audio/webm'
        );
        mimeType = 'audio/webm';
      }

      console.log('Using MIME type:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        handleRecordingStop();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      onError(null);
      if (onProcessingStart) {
        onProcessingStart();
      }

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      // Start audio level visualization
      updateAudioLevel();
    } catch (error) {
      let errorMessage = 'Microphone access error';

      if (error.name === 'NotAllowedError') {
        errorMessage =
          'Microphone access denied. Please allow microphone access to use this feature.';
      } else if (error.name === 'NotFoundError') {
        errorMessage =
          'Microphone not found. Please ensure you have a microphone connected.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage =
          'Your browser does not support audio recording. Please use Chrome, Firefox, Edge, or Safari.';
      }

      onError(errorMessage);
      console.error('Error accessing microphone:', error);
    }
  };

  /**
   * Update audio level for visualization
   */
  const updateAudioLevel = () => {
    if (analyserRef.current && dataArrayRef.current) {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const average =
        dataArrayRef.current.reduce((a, b) => a + b) /
        dataArrayRef.current.length;
      setAudioLevel((average / 255) * 100);

      if (isRecording) {
        animationIdRef.current = requestAnimationFrame(updateAudioLevel);
      }
    }
  };

  /**
   * Stop recording audio
   */
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();

      // Stop all audio tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Cancel animation frame
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }

      setIsRecording(false);
      setAudioLevel(0);
    }
  };

  /**
   * Handle the end of recording and send to API
   */
  const handleRecordingStop = async () => {
    setIsProcessing(true);

    try {
      if (chunksRef.current.length === 0) {
        throw new Error('No audio recorded. Please try again.');
      }

      // Create audio blob
      const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

      console.log('Audio blob size:', audioBlob.size, 'bytes');
      console.log('Number of chunks:', chunksRef.current.length);
      console.log('Recording duration:', recordingTime, 'seconds');

      if (audioBlob.size === 0) {
        throw new Error('Audio file is empty. Please try recording again.');
      }

      // Create a test download link to verify audio was recorded
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('Audio blob URL (for testing):', audioUrl);

      // Send to API (try with original WebM blob first)
      onError(null);
      const result = await transcribeAudio(audioBlob, {
        language_code: 'en-IN', // Change as needed
        model: 'saarika:v2.5',
      });

      console.log('API Response received:', result);

      // Format response with metadata
      const formattedResponse = {
        text: result.transcript || '',
        metadata: {
          source: 'Speech-to-Text',
          request_id: result.request_id,
          language_code: result.language_code,
          language_probability: result.language_probability,
          timestamps: result.timestamps,
          diarized_transcript: result.diarized_transcript,
          duration_seconds: recordingTime
        }
      };

      // Pass transcript to parent component
      onTranscriptReceived(formattedResponse);
    } catch (error) {
      onError(`Transcription failed: ${error.message}`);
      console.error('Transcription error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Convert WebM audio to WAV format
   * Simple conversion for better API compatibility
   */
  const convertToWAV = async (webmBlob) => {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const arrayBuffer = await webmBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Create WAV file
      const numberOfChannels = audioBuffer.numberOfChannels;
      const sampleRate = audioBuffer.sampleRate;
      const format = 1; // PCM
      const bitDepth = 16;

      const bytesPerSample = bitDepth / 8;
      const blockAlign = numberOfChannels * bytesPerSample;

      const rawData = new Float32Array(audioBuffer.length * numberOfChannels);
      const channelData = [];

      for (let i = 0; i < numberOfChannels; i++) {
        channelData.push(audioBuffer.getChannelData(i));
      }

      let offset = 0;
      for (let i = 0; i < audioBuffer.length; i++) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
          rawData[offset++] = channelData[channel][i];
        }
      }

      const wavFileSize = 44 + rawData.length * 2;
      const wavFile = new ArrayBuffer(wavFileSize);
      const view = new DataView(wavFile);

      // WAV file header
      const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      };

      writeString(0, 'RIFF');
      view.setUint32(4, wavFileSize - 8, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true); // fmt chunk size
      view.setUint16(20, format, true);
      view.setUint16(22, numberOfChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * blockAlign, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, bitDepth, true);

      writeString(36, 'data');
      view.setUint32(40, rawData.length * 2, true);

      // PCM data
      offset = 44;
      for (let i = 0; i < rawData.length; i++) {
        const sample = Math.max(-1, Math.min(1, rawData[i]));
        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true
        );
        offset += 2;
      }

      return new Blob([wavFile], { type: 'audio/wav' });
    } catch (error) {
      console.error('Error converting to WAV:', error);
      // Fallback: return original blob
      return webmBlob;
    }
  };

  /**
   * Format recording time in MM:SS format
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-recorder">
      <div className="recorder-header">
        <h2>🎙️ Speech to Text Converter</h2>
        <p>Record your audio and convert it to text instantly</p>
      </div>

      <div className="recorder-controls">
        <button
          onClick={startRecording}
          disabled={isRecording || isProcessing || isDisabled}
          className="btn btn-start"
          title="Start recording"
        >
          ● Start Recording
        </button>

        <button
          onClick={stopRecording}
          disabled={!isRecording || isProcessing}
          className="btn btn-stop"
          title="Stop recording"
        >
          ⏹ Stop Recording
        </button>
      </div>

      {isRecording && (
        <div className="audio-level-container">
          <div className="audio-level-bar">
            <div
              className="audio-level-fill"
              style={{ width: `${audioLevel}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="recorder-status">
        {isRecording && (
          <div className="status-indicator recording">
            <span className="pulse"></span>
            Recording... {formatTime(recordingTime)}
          </div>
        )}

        {isProcessing && (
          <div className="status-indicator processing">
            <span className="spinner"></span>
            Processing audio... Converting to text
          </div>
        )}

        {!isRecording && !isProcessing && recordingTime > 0 && (
          <div className="status-indicator complete">
            ✓ Ready for new recording
          </div>
        )}
      </div>
    </div>
  );
}

export default AudioRecorder;